import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Stack, TextField } from "~/common/mui";
import { resolveAsmMessage } from "~/features/assistedService/utils/asmMessage";

interface Props {
  pending: boolean;
  alert: { messageCode: string; alertClass: string | null } | null;
  onLogin: (username: string, password: string) => void;
  className?: string;
  sx?: object;
}

/** ASM agent login form. Posts `LoginRequest` via the parent's `onLogin` callback. */
export const AsmLoginForm = ({ pending, alert, onLogin, className, sx }: Props) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Credential errors (ASM_alert_cred) highlight both fields, mirroring the legacy `${error}` class.
  const credError = alert?.alertClass?.includes("ASM_alert_cred") ?? false;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!username || !password || pending) {
      return;
    }
    onLogin(username, password);
  };

  return (
    <Stack className={className} sx={sx} component="form" onSubmit={submit} spacing={2}>
      {alert && <Alert severity="error">{resolveAsmMessage(t, alert.messageCode)}</Alert>}
      <Stack direction={{ phone: "column", tablet: "row" }} spacing={2}>
        <TextField
          label={t("asm.login.username")}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          size="small"
          error={credError}
        />
        <TextField
          label={t("asm.login.password")}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          size="small"
          error={credError}
        />
        <Box>
          <Button type="submit" variant="contained" disabled={pending}>
            {t("asm.login.submit")}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};
